import { FC, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WORKFLOW_RUN_TERMINAL_STATES } from 'dashboard/models/workflow-run';
import {
  WorkflowRunSimulateContext,
  WorkflowRunSimulated,
} from 'dashboard/models/workflow-run-simulated';
import { setAndPersistCurrentEnvironment } from 'dashboard/redux/thunk-actions/current-environment';
import {
  createSimulatedWorkflowRun,
  pollForWorkflowRunComplete,
  runSimulatedWorkflowRun,
} from 'dashboard/redux/thunk-actions/workflow-run';
import { DashboardWorkflowRunsShowRouteProps } from 'dashboard/routes/DashboardWorkflowRunsShowRoute/DashboardWorkflowRunsShowRoute';
import DashboardWorkflowRunsSimulateShowView from 'dashboard/views/DashboardWorkflowRunsSimulateShowView';

type DashboardWorkflowSimulatedRunsShowRouteProps = DashboardWorkflowRunsShowRouteProps & {
  workflowRun: WorkflowRunSimulated;
} & PropsFromRedux;

const DashboardWorkflowSimulatedRunsShowRoute: FC<DashboardWorkflowSimulatedRunsShowRouteProps> = ({
  createSimulatedWorkflowRun,
  currentEnvironment,
  environments,
  inputStep,
  knownObjects,
  pollForWorkflowRunComplete,
  runSimulatedWorkflowRun,
  setAndPersistCurrentEnvironment,
  showMessage,
  steps,
  triggerObject,
  workflow,
  workflowSchema,
  workflowVersion,
  workflowRun,
}) => {
  const navigate = useNavigate();
  const [simulateContext, setSimulateContext] = useState<WorkflowRunSimulateContext>(
    workflowRun.attributes['simulate-context'] || {},
  );
  const [simulationTimedOut, setSimulationTimedOut] = useState(false);
  const clearPollTimeoutRef = useRef<(() => void) | null>(null);

  const pollWorkflowRun = async (workflowRun: WorkflowRunSimulated) => {
    clearPollTimeoutRef.current = await pollForWorkflowRunComplete(
      workflowRun.id,
      () => {
        showMessage('Simulation completed', MessageType.Success);
      },
      () => {
        setSimulationTimedOut(true);
        showMessage('Simulation timed out', MessageType.Failure);
      },
    );
  };

  useEffect(() => {
    if (
      workflowRun.attributes['started-at'] != null &&
      !WORKFLOW_RUN_TERMINAL_STATES.includes(workflowRun.attributes.status)
    ) {
      pollWorkflowRun(workflowRun);
    }

    return () => {
      if (clearPollTimeoutRef.current) {
        clearPollTimeoutRef.current();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRun = async () => {
    //restart a simulated run if timed out or previous run reached a terminal status
    if (
      simulationTimedOut ||
      WORKFLOW_RUN_TERMINAL_STATES.includes(workflowRun.attributes.status)
    ) {
      createSimulatedWorkflowRun(
        workflowVersion.relationships.workflow.data.id,
        workflowVersion.id,
        navigate,
        simulateContext,
      );
    } else {
      runSimulatedWorkflowRun(workflowRun, navigate, simulateContext);
      showMessage('Simulation started', MessageType.Success);
      pollWorkflowRun(workflowRun);
    }
  };

  return (
    <DashboardWorkflowRunsSimulateShowView
      backRoute={`/dashboard/workflows/${workflow.id}/${workflowVersion.id}`}
      knownObjects={knownObjects}
      inputStep={inputStep}
      triggerObject={triggerObject}
      setSimulateContext={setSimulateContext}
      simulateContext={simulateContext}
      steps={steps}
      workflowSchema={workflowSchema}
      workflowVersion={workflowVersion}
      workflow={workflow}
      workflowRun={workflowRun}
      currentEnvironment={currentEnvironment}
      environments={environments}
      setAndPersistCurrentEnvironment={setAndPersistCurrentEnvironment}
      simulationTimedOut={simulationTimedOut}
      handleRun={handleRun}
    />
  );
};

const mapDispatchToProps = {
  createSimulatedWorkflowRun,
  pollForWorkflowRunComplete,
  runSimulatedWorkflowRun,
  setAndPersistCurrentEnvironment,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWorkflowSimulatedRunsShowRoute);
